import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Ban,
  Building2,
  ClipboardCheck,
  ExternalLink,
  Loader2,
  MapPin,
  PackageCheck,
  PackageOpen,
  Pencil,
  Printer,
  Warehouse,
} from 'lucide-react';
import clsx from 'clsx';
import { DetailBreadcrumb } from '../../../../components/ui/DetailBreadcrumb';
import { DetailHero } from '../../../../components/ui/DetailHero';
import { DataTable } from '../../../../components/ui/DataTable';
import { StatCard } from '../../../../components/ui/StatCard';
import { Button } from '../../../../components/ui/Button';
import { ToneBadge } from '../../../../components/ui/ToneBadge';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { ErrorState } from '../../../../components/ui/ErrorState';
import { StaleDataNotice } from '../../../../components/ui/StaleDataNotice';
import { useNotice } from '../../../../hooks/useNotice';
import { DeclareReceiptModal } from './modals/DeclareReceiptModal';
import { ReceiveReceiptModal } from './modals/ReceiveReceiptModal';
import {
  cancelReceipt,
  closeReceipt,
  fetchReceipt,
  fetchReceiptManifest,
} from '../../../../features/receipts/receiptsThunks';
import { clearReceiptDetail } from '../../../../features/receipts/receiptsSlice';
import {
  RECEIPT_STATUS,
  RECEIPT_STATUS_TONES,
} from '../../../../features/receipts/receiptsConstants';
import {
  cancelMessage,
  closeMessage,
  formatReceiptMoment,
  isOpenReceipt,
  unitsOf,
} from '../../../../features/receipts/receiptsUtils';
import { selectCanWms } from '../../../../features/auth/authSelectors';
import {
  formatQuantity,
  receiptEventLabel,
  receiptStatusLabel,
} from '../../../../utils/inventoryLabels';

const RECEIPTS_PATH = '/warehouse/receipts';
const LINES_PER_PAGE = 20;

const selectCanDeclare = selectCanWms('inventory:receive');

const Fact = ({ label, children }) => (
  <div className="space-y-1 min-w-0">
    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">{label}</span>
    <p className="font-medium text-ninja-dark dark:text-white break-words">{children}</p>
  </div>
);

/**
 * `/warehouse/receipts/:receiptId`: one receiving order — what was expected,
 * what arrived, and everything that was done to it.
 *
 * This is where the acts that are not "going in to look" live: closing with a
 * shortage, editing while nothing was counted, voiding, and the printable
 * manifest. Each one is drawn only for who may do it, on the word of the server.
 */
export const ReceiptDetail = () => {
  const { receiptId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { detail, detailCanConfirm, detailLoading, detailError, acting, printing } = useSelector(
    (state) => state.receipts
  );
  const canDeclare = useSelector(selectCanDeclare);

  const [isReceiving, setIsReceiving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  /** `'close'`, `'cancel'` or `null`. */
  const [confirming, setConfirming] = useState(null);
  const [linesPage, setLinesPage] = useState(1);
  const [linesPerPage, setLinesPerPage] = useState(LINES_PER_PAGE);
  const setNotice = useNotice('receipt-detail-notice');

  const loadReceipt = useCallback(() => dispatch(fetchReceipt(receiptId)), [dispatch, receiptId]);

  useEffect(() => {
    loadReceipt();
    return () => dispatch(clearReceiptDetail());
  }, [loadReceipt, dispatch]);


  // The id is compared too: while the next order loads, the previous one is
  // still in the store and must not be drawn under the new address.
  const isCurrent = Boolean(detail) && String(detail.id) === String(receiptId);

  // A failed reload of the order on screen leaves it there, under a strip.
  if (detailError && !isCurrent) {
    return (
      <ErrorState
        title="No se pudo abrir la recepción"
        description={detailError}
        onRetry={loadReceipt}
      >
        <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate(RECEIPTS_PATH)}>
          Volver a Recepción
        </Button>
      </ErrorState>
    );
  }

  if (detailLoading || !isCurrent) {
    return (
      <div data-testid="receipt-detail-loading" className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-ninja-green animate-spin" />
      </div>
    );
  }

  const receipt = detail;
  const units = unitsOf(receipt);
  const isCancelled = receipt.status === RECEIPT_STATUS.CANCELLED;

  const canReceive = detailCanConfirm && isOpenReceipt(receipt);
  const canClose = detailCanConfirm && receipt.status === RECEIPT_STATUS.PARTIAL;
  // Only while nothing was counted: after that, what was declared is history.
  const canEdit = canDeclare && receipt.status === RECEIPT_STATUS.PENDING;
  // A closed order can be voided too, for as long as the goods are still whole
  // in the receiving zone. The server checks it and, if part was put away, says so.
  const canCancel = detailCanConfirm && !isCancelled;

  const handlePrint = async () => {
    const action = await dispatch(fetchReceiptManifest(receipt.id));
    if (action.meta.requestStatus === 'fulfilled') {
      window.open(action.payload, '_blank', 'noopener,noreferrer');
    } else {
      setNotice({ type: 'error', message: action.payload });
    }
  };

  const handleConfirmed = async () => {
    const isClosing = confirming === 'close';
    const action = await dispatch(
      isClosing ? closeReceipt(receipt.id) : cancelReceipt(receipt.id)
    );
    setConfirming(null);
    setNotice(
      action.meta.requestStatus === 'fulfilled'
        ? {
            type: 'success',
            message: isClosing
              ? `${receipt.code} cerrada, con su faltante registrado.`
              : `${receipt.code} anulada. Su historial se conserva.`,
          }
        : { type: 'error', message: action.payload }
    );
  };

  const lines = receipt.lines || [];
  const lastPage = Math.max(1, Math.ceil(lines.length / linesPerPage));
  const page = Math.min(linesPage, lastPage);
  const pageLines = lines.slice((page - 1) * linesPerPage, page * linesPerPage);

  const lineColumns = [
    { header: '#', cell: (row) => <span className="text-text-muted">{row.line_no}</span> },
    {
      header: 'PRODUCTO',
      cell: (row) => (
        <div className="flex flex-col max-w-[320px]">
          <span className="font-bold font-mono text-ninja-dark dark:text-white">
            {row.product_sku}
          </span>
          <span className="text-xs text-text-muted mt-0.5 truncate" title={row.product_name}>
            {row.product_name}
          </span>
        </div>
      ),
    },
    {
      header: 'LOTE',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-mono">{row.lot || '-'}</span>
          {row.expiry_date && (
            <span className="text-xs text-text-muted mt-0.5">Vence {row.expiry_date}</span>
          )}
        </div>
      ),
    },
    {
      header: 'ESPERADO',
      cell: (row) => <span className="tabular-nums">{formatQuantity(row.expected)}</span>,
    },
    {
      header: 'RECIBIDO',
      cell: (row) => (
        <span className="font-semibold tabular-nums">{formatQuantity(row.received)}</span>
      ),
    },
    {
      header: 'DIFERENCIA',
      cell: (row) => {
        const difference = Number(row.received) - Number(row.expected);
        return (
          <span
            data-testid="receipt-line-difference"
            className={clsx(
              'font-semibold tabular-nums',
              difference < 0 && 'text-danger',
              difference > 0 && 'text-warning',
              difference === 0 && 'text-text-muted'
            )}
          >
            {difference > 0 ? '+' : ''}
            {formatQuantity(difference)}
          </span>
        );
      },
    },
  ];

  return (
    <div data-testid="receipt-detail-page" className="space-y-6 animate-in fade-in duration-300">
      <DetailBreadcrumb backLabel="Volver a Recepción" to={RECEIPTS_PATH} current={receipt.code} />

      {detailError && (
        <StaleDataNotice testId="receipt-detail-stale-error" error={detailError} onRetry={loadReceipt} />
      )}

      <DetailHero
        media={<PackageOpen size={36} />}
        title={receipt.code}
        badges={
          <ToneBadge tone={RECEIPT_STATUS_TONES[receipt.status]} testId="receipt-status">
            {receiptStatusLabel(receipt.status)}
          </ToneBadge>
        }
        meta={
          <>
            <span className="flex items-center gap-1.5">
              <Building2 size={14} /> {receipt.company_name}
            </span>
            <span className="flex items-center gap-1.5">
              <Warehouse size={14} /> {receipt.warehouse_name}
            </span>
            <span className="flex items-center gap-1.5">
              <MapPin size={14} /> {receipt.slot_name || 'Ubicación por definir al recepcionar'}
            </span>
          </>
        }
        actions={
          <>
            {canReceive && (
              <Button
                variant="primary"
                icon={PackageCheck}
                onClick={() => setIsReceiving(true)}
                data-testid="receipt-receive"
              >
                Recepcionar
              </Button>
            )}
            {canClose && (
              <Button
                variant="ghost"
                icon={ClipboardCheck}
                onClick={() => setConfirming('close')}
                data-testid="receipt-close"
              >
                Cerrar con faltante
              </Button>
            )}
            {canEdit && (
              <Button
                variant="ghost"
                icon={Pencil}
                onClick={() => setIsEditing(true)}
                data-testid="receipt-edit"
              >
                Editar
              </Button>
            )}
            <Button
              variant="ghost"
              icon={Printer}
              iconRight={ExternalLink}
              loading={printing}
              loadingLabel="Generando..."
              onClick={handlePrint}
              data-testid="receipt-print"
            >
              Manifiesto
            </Button>
            {canCancel && (
              <Button
                variant="danger"
                icon={Ban}
                onClick={() => setConfirming('cancel')}
                data-testid="receipt-cancel"
              >
                Anular
              </Button>
            )}
          </>
        }
      />

      <div data-testid="receipt-units" className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <StatCard
          title="Esperado"
          value={formatQuantity(units.expected)}
          subtitle="Lo que se declaró que venía"
        />
        <StatCard
          title="Recibido"
          value={formatQuantity(units.received)}
          tone={units.received > 0 ? 'success' : undefined}
          icon={PackageCheck}
          subtitle="Lo contado: es lo que entró al stock"
        />
        <StatCard
          title="Faltante"
          value={formatQuantity(units.missing)}
          tone={units.missing > 0 && !isOpenReceipt(receipt) ? 'danger' : undefined}
          icon={ClipboardCheck}
          subtitle={
            isOpenReceipt(receipt) ? 'Todavía se puede recibir' : 'Quedó registrado al cerrar'
          }
        />
      </div>

      <section className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border">
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-6">
          <Fact label="Origen">
            <span data-testid="receipt-source">{receipt.source_display || '-'}</span>
          </Fact>
          <Fact label="Proveedor">{receipt.supplier || '-'}</Fact>
          <Fact label="Referencia">{receipt.reference || '-'}</Fact>
          <Fact label="Creada">{formatReceiptMoment(receipt.created_at)}</Fact>
          <Fact label="Por">{receipt.created_by_email || '-'}</Fact>
          <Fact label="Cerrada">{formatReceiptMoment(receipt.closed_at)}</Fact>
        </div>
        {receipt.notes && (
          <p data-testid="receipt-notes-text" className="text-sm text-text-secondary mt-6">
            <span className="font-semibold text-ninja-dark dark:text-white">Notas:</span>{' '}
            {receipt.notes}
          </p>
        )}
      </section>

      <DataTable
        columns={lineColumns}
        data={pageLines}
        totalItems={lines.length}
        currentPage={page}
        itemsPerPage={linesPerPage}
        itemLabel="líneas"
        emptyMessage="Esta orden no tiene líneas."
        onPageChange={setLinesPage}
        onRowsPerPageChange={(rowsPerPage) => {
          setLinesPerPage(rowsPerPage);
          setLinesPage(1);
        }}
      />

      {receipt.events?.length > 0 && (
        <section
          data-testid="receipt-history"
          className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 shadow-sm border border-border"
        >
          <h2 className="text-lg font-bold text-ninja-dark dark:text-white mb-4">Historial</h2>
          <ul className="divide-y divide-border">
            {receipt.events.map((event) => (
              <li
                key={event.id}
                data-testid="receipt-event"
                className="py-3 flex flex-col sm:flex-row sm:items-baseline gap-x-4 gap-y-1"
              >
                <span className="text-xs text-text-muted sm:w-36 flex-shrink-0">
                  {formatReceiptMoment(event.created_at)}
                </span>
                <span className="text-sm font-semibold text-ninja-dark dark:text-white sm:w-24 flex-shrink-0">
                  {receiptEventLabel(event.type)}
                </span>
                <span className="text-sm text-text-secondary flex-1 min-w-0 break-words">
                  {event.detail}
                </span>
                {event.actor_email && (
                  <span className="text-xs text-text-muted">{event.actor_email}</span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      <ReceiveReceiptModal
        receipt={isReceiving ? receipt : null}
        onClose={() => setIsReceiving(false)}
        onDone={setNotice}
      />

      <DeclareReceiptModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        warehouse={null}
        receipt={receipt}
        clientOptions={[{ value: receipt.company, label: receipt.company_name }]}
        onSaved={(message) => setNotice({ type: 'success', message })}
      />

      {/* Both say what they do with numbers: how much is missing, how much is
          reverted, and that nothing is deleted. */}
      <ConfirmDialog
        isOpen={confirming === 'close'}
        onClose={() => setConfirming(null)}
        onConfirm={handleConfirmed}
        title={`Cerrar ${receipt.code} con faltante`}
        message={closeMessage(receipt)}
        confirmLabel="Cerrar con faltante"
        loadingLabel="Cerrando..."
        loading={acting === 'close'}
        testId="receipt-close-dialog"
      />
      <ConfirmDialog
        isOpen={confirming === 'cancel'}
        onClose={() => setConfirming(null)}
        onConfirm={handleConfirmed}
        title={`Anular ${receipt.code}`}
        message={cancelMessage(receipt)}
        confirmLabel="Anular"
        loadingLabel="Anulando..."
        loading={acting === 'cancel'}
        testId="receipt-cancel-dialog"
      />

    </div>
  );
};
